import { useEffect, useMemo, useRef, useState } from 'react';
import { StyledMark } from '@/modules/components/StyledMark/StyledMark.styles';
import {
  handleAccordionClose,
  handleAccordionOpen,
} from '@/analytics/eventHelpers/accordion';
import { SpecItem } from '@tribal-worldwide-london/fa-configuration-service-vwg';
import { StockSpecItem } from '@tribal-worldwide-london/fa-stock-service-vwg';
import { EventCommonProps } from '@/analytics/events';
import type { SpecCategories } from '@/packages/api/interfaces/SpecCategories';

export const useSpecSearch = ({
  categories,
  mode,
  analyticsCommonFields,
}: {
  categories: SpecCategories;
  mode: 'single' | 'compare';
  analyticsCommonFields: EventCommonProps;
}) => {
  const searchRef = useRef<HTMLInputElement>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [manualToggle, setManualToggle] = useState<boolean | undefined>(true);
  const [isSpecDetailOpen, setIsSpecDetailOpen] = useState<
    Record<number, boolean>
  >({});

  const reg = new RegExp(searchTerm, 'gi');

  const renderTextWithHighlights = (text: string) => {
    const regex = new RegExp(searchTerm, 'gi');
    const newText = text.replace(regex, `<mark>$&</mark>`);

    if (!searchTerm) return text;
    return <StyledMark dangerouslySetInnerHTML={{ __html: newText }} />;
  };

  const foundCategories = useMemo(() => {
    if (!categories) return [];

    interface FoundCategory {
      key: string;
      value: SpecItem[] | [string, StockSpecItem[]][];
      index: number;
    }

    const mappedCategories = categories.map(
      ([key, value], index: number): FoundCategory => ({
        key,
        value,
        index,
      })
    );

    const filteredCategories = mappedCategories.filter(
      ({ key, value }: FoundCategory) => {
        const keyMatches = key?.match(reg);
        const valueMatches: boolean =
          mode === 'single'
            ? (value as SpecItem[]).some((item: SpecItem) =>
                item.title?.match(reg)
              )
            : Object.entries(
                value as unknown as Record<string, StockSpecItem[]>
              ).some(([key]) => key?.match(reg));
        return keyMatches || valueMatches;
      }
    );

    const transformedCategories = filteredCategories.map(
      (item: FoundCategory) => ({
        ...item,
        value:
          mode === 'single'
            ? (item.value as SpecItem[]).filter((item) =>
                item.title?.match(reg)
              )
            : Object.entries(
                item.value as unknown as Record<string, StockSpecItem[]>
              )?.filter(([key]) => key?.match(reg)),
      })
    );

    return transformedCategories;
  }, [searchTerm, categories, mode]);

  const handleToggleAccordion = (
    index: number,
    groupItemName: string,
    manualToggle?: boolean
  ) => {
    setManualToggle(manualToggle);
    setIsSpecDetailOpen((prevState) => ({
      ...prevState,
      [index]: !prevState[index],
    }));

    if (!isSpecDetailOpen[index]) {
      handleAccordionOpen(analyticsCommonFields, {
        groupItemName,
        groupItemState: 'open',
      });
    } else {
      handleAccordionClose(analyticsCommonFields, {
        groupItemName,
        groupItemState: 'closed',
      });
    }
  };

  const handleSubmitSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setManualToggle(false);
    const value = searchRef.current?.value || '';
    setSearchTerm(value);
    if (!value) setIsSpecDetailOpen({});
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setManualToggle(true);
    searchRef.current?.value && (searchRef.current.value = '');
    setIsSpecDetailOpen({});
  };

  const error = useMemo(
    () => (foundCategories.length === 0 ? 'No results found' : null),
    [foundCategories]
  );

  useEffect(() => {
    if (manualToggle) return;
    if (foundCategories.length && searchTerm) {
      foundCategories.forEach((category) => {
        if (category.value.length && !isSpecDetailOpen[category.index]) {
          handleToggleAccordion(category.index, category.key);
        } else if (!category.value.length && isSpecDetailOpen[category.index]) {
          handleToggleAccordion(category.index, category.key);
        }
      });
    }

    Object.keys(isSpecDetailOpen).forEach((key) => {
      const index = Number(key);
      const categoryMatch = foundCategories.some(
        (category) => category.index === index
      );

      if (error) return;

      if (isSpecDetailOpen[index] && !categoryMatch) {
        handleToggleAccordion(index, key);
      }
    });
  }, [foundCategories, searchTerm, isSpecDetailOpen, manualToggle]);

  return {
    error,
    foundCategories,
    renderTextWithHighlights,
    searchRef,
    searchTerm,
    setSearchTerm,
    manualToggle,
    setManualToggle,
    isSpecDetailOpen,
    setIsSpecDetailOpen,
    handleSubmitSearch,
    handleClearSearch,
    handleToggleAccordion,
  };
};
